import { useEffect, useState, useRef } from "react";
import "./NewsletterData.css";
import { BannerSkeleton } from "../../BannerSkeleton";
import { isNavigable } from "../../../../Utils/StayUpToDate/RenderComponent";
import { isHexDark } from "../../../../Utils/HelperFunction/HelperFunction";

const FLODESK_FORM_SELECTOR = '[class*="__form"]';
const POLL_INTERVAL = 200;
const POLL_TIMEOUT = 10000;

const STYLE_PROPS = {
  backgroundColor: ["background-color", "background"],
  color: ["color"],
  fontFamily: ["font-family"],
} as const;

type StyleProperty = keyof typeof STYLE_PROPS;

const FALLBACK_ATTR: Record<StyleProperty, string> = {
  backgroundColor: "data-fallback-bg",
  color: "data-fallback-color",
  fontFamily: "data-fallback-font",
};

const isUndefinedValue = (value: string) =>
  !value ||
  value === "rgba(0, 0, 0, 0)" ||
  value === "transparent" ||
  value === "inherit" ||
  value === "initial" ||
  value === "none";

const isFlodeskSelector = (selector: string) =>
  /\[data-ff-el|\.ff-[a-f0-9]|__button|\.fd-btn|flodesk/i.test(selector);

/** True if a Flodesk <style>/stylesheet rule already sets this property on the element. */
const getFlodeskStylesheetValue = (
  el: HTMLElement,
  property: StyleProperty
): string | null => {
  const cssProps = STYLE_PROPS[property];
  let found: string | null = null;

  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      continue; // cross-origin sheets
    }

    for (const rule of Array.from(rules)) {
      if (!(rule instanceof CSSStyleRule) || !rule.selectorText) continue;
      if (!isFlodeskSelector(rule.selectorText)) continue;

      let propValue = "";
      for (const cssProp of cssProps) {
        propValue = rule.style.getPropertyValue(cssProp);
        if (propValue) break;
      }
      if (!propValue || isUndefinedValue(propValue.trim())) continue;

      try {
        if (el.matches(rule.selectorText)) {
          found = propValue.trim();
        }
      } catch {
        // selector not valid for Element.matches
      }
    }
  }

  return found;
};

const applyFallback = (
  el: HTMLElement,
  property: StyleProperty,
  fallbackValue?: string | null
) => {
  if (!fallbackValue) return;

  const attr = FALLBACK_ATTR[property];
  const fromSheet = getFlodeskStylesheetValue(el, property);

  // Flodesk class CSS wins — strip inline overrides so it can apply
  if (fromSheet) {
    if (el.style[property]) {
      el.style[property] = "";
    }
    el.removeAttribute(attr);
    return;
  }

  // Flodesk (or other) inline that we didn't set — leave it alone
  if (!el.hasAttribute(attr)) {
    const inline = el.style[property];
    if (inline && !isUndefinedValue(inline)) return;
  }

  el.style[property] = fallbackValue;
  el.setAttribute(attr, "1");
};

/**
 * Walks the rendered Flodesk form and fills in colors/fonts ONLY where
 * Flodesk hasn't already defined them (stylesheet class rules OR inline).
 * Two fallback themes:
 * - "default" template: black buttons (untouched), white button-span text, black div text
 * - any other template: forthColor buttons, secondaryColor button/span/div text
 */
const applyFallbackTheme = (
  form: HTMLElement,
  isDefaultTemplate: boolean,
  designTokens: any
) => {
  // --- Buttons ---
  form.querySelectorAll<HTMLElement>("button").forEach((btn) => {
    if (isDefaultTemplate) {
      // background already black via existing styling — leave it alone
      btn.querySelectorAll<HTMLElement>("span").forEach((span) => {
        applyFallback(span, "color", "#ffffff");
      });
    } else {
      applyFallback(btn, "backgroundColor", designTokens?.forthColor);
      applyFallback(btn, "color", designTokens?.secondaryColor);

      // Span label should follow the button color (Flodesk CSS or our fallback)
      btn.querySelectorAll<HTMLElement>("span").forEach((span) => {
        span.style.color = "inherit";
        span.removeAttribute(FALLBACK_ATTR.color);
      });
    }
  });

  // --- Generic div text (e.g. <div>text</div>) ---
  form.querySelectorAll<HTMLElement>("div").forEach((div) => {
    // Skip divs that are just containers for buttons — button case above
    // already owns text color there, no need to double-apply.
    if (div.closest("button")) return;

    const ink = designTokens?.secondaryColor || "#000000";
    const fallbackColor =
      isDefaultTemplate || !isHexDark(ink) ? "#000000" : ink;
    applyFallback(div, "color", fallbackColor);
  });

  // Input values + placeholders must stay readable on Flodesk's white fields.
  const fieldColor = isDefaultTemplate || !isHexDark(designTokens?.secondaryColor || "#111")
    ? "#4a4a4a"
    : designTokens?.secondaryColor;
  form.querySelectorAll<HTMLInputElement>("input").forEach((input) => {
    input.style.setProperty("color", "#1a1814", "important");
    input.style.setProperty("-webkit-text-fill-color", "#1a1814", "important");
    input.style.setProperty("caret-color", "#1a1814", "important");
    input.style.setProperty("opacity", "1", "important");
  });
  const styleId = "fd-form-placeholder-contrast";
  if (!form.querySelector(`#${styleId}`)) {
    const sheet = document.createElement("style");
    sheet.id = styleId;
    sheet.textContent = `
      input::placeholder,
      input::-webkit-input-placeholder {
        color: ${fieldColor} !important;
        -webkit-text-fill-color: ${fieldColor} !important;
        opacity: 1 !important;
      }
      label, [class*="placeholder"], [class*="label"] {
        color: ${fieldColor} !important;
        opacity: 1 !important;
      }
    `;
    form.prepend(sheet);
  }
};

const NewsletterData = (props: any) => {
  const [loading, setLoading] = useState(true);
  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    setTimeout(() => setLoading(false), 400);
  }, []);

  const {
    title,
    component,
    brand_color,
    flowdeskId,
    isHandleNavigate,
    from,
    designTokens,
  } = props;

  const isDefaultTemplate = (designTokens?.templateName as string)?.toLowerCase() === "default";

  useEffect(() => {
    (function (w: any, d: Document, t: string, h: string, s: string, n: string) {
      w.FlodeskObject = n;
      const fn = function (...args: any[]) {
        (w[n].q = w[n].q || []).push(args);
      };
      w[n] = w[n] || fn;
      const f: any = d.getElementsByTagName(t)[0];
      const v = "?v=" + Math.floor(new Date().getTime() / (120 * 1000)) * 60;

      const sm: any = d.createElement(t);
      sm.async = true;
      sm.type = "module";
      sm.src = h + s + ".mjs" + v;
      f.parentNode.insertBefore(sm, f);

      const sn: any = d.createElement(t);
      sn.async = true;
      sn.noModule = true;
      sn.src = h + s + ".js" + v;
      f.parentNode.insertBefore(sn, f);
    })(window, document, "script", "https://assets.flodesk.com", "/universal", "fd");

    if (window.fd) {
      window.fd("form", {
        formId: `${flowdeskId}`,
        containerEl: `#fd-form-${flowdeskId}`,
      });
    }
    setLoading(false);
  }, [loading, flowdeskId]);

  useEffect(() => {
    const start = Date.now();
    let mounted = true;
    const retryTimers: ReturnType<typeof setTimeout>[] = [];

    const apply = (form: HTMLElement) => {
      if (mounted) applyFallbackTheme(form, isDefaultTemplate, designTokens);
    };

    const interval = setInterval(() => {
      const form = document.querySelector<HTMLElement>(FLODESK_FORM_SELECTOR);
      if (form) {
        clearInterval(interval);

        // Apply once the form first appears...
        apply(form);

        // Flodesk often injects its <style> slightly after the form DOM.
        // Re-check so stylesheet colors/fonts can replace our fallbacks;
        // if Flodesk never defines them, these passes keep our tokens.
        [300, 1000, 2500].forEach((ms) => {
          retryTimers.push(setTimeout(() => apply(form), ms));
        });

        // ...and keep re-applying if Flodesk mutates the form later
        // (e.g. async content, validation states, multi-step forms).
        observerRef.current = new MutationObserver(() => apply(form));
        observerRef.current.observe(form, {
          childList: true,
          subtree: true,
        });
      } else if (Date.now() - start > POLL_TIMEOUT) {
        clearInterval(interval);
      }
    }, POLL_INTERVAL);

    return () => {
      mounted = false;
      clearInterval(interval);
      retryTimers.forEach(clearTimeout);
      observerRef.current?.disconnect();
    };
  }, [designTokens, isDefaultTemplate]);

  return (
    <>
      {loading ? (
        <BannerSkeleton designTokens={designTokens} />
      ) : (
        <>
          <div className="mainDiv">
            <p
              className="component-name"
              style={{ cursor: isNavigable(component) ? "pointer" : "default", fontFamily: designTokens?.primaryFontFamily ?? designTokens?.fontFamily }}
            >
              {component.name}
            </p>
          </div>

          <div
            className="outter-section-banner-subcomponent"
            style={{
              marginTop: "0px",
              backgroundColor: designTokens?.primaryColor,
              width: "100%",
              minHeight: "250px",
              color:
                isDefaultTemplate ||
                !isHexDark(designTokens?.secondaryColor || "#111")
                  ? "#1a1814"
                  : designTokens?.secondaryColor,
              border: `1px solid ${designTokens?.tertiaryColor}`,
            }}
          >
            <div id={`fd-form-${flowdeskId}`}></div>
          </div>
        </>
      )}
    </>
  );
};

export default NewsletterData;